import { alpha } from '@mui/material/styles';

export default theme => ({
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        margin: 'auto',
        width: '100%',
    },
    card: {
        borderRadius: 10,
        padding: theme.spacing(3),
    },
    cardTitle: {
        fontWeight: 600,
    },
    cardDescription: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    cardDivider: {
        margin: theme.spacing(2, 0),
    },
    // Rows stack vertically with even spacing. Normal rows are separated by an
    // explicit <Divider>; switch rows render as bordered toggle cards, so the
    // gap alone separates them (no divider crowding the card border).
    rows: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    // `inline` groups lay fields side by side and wrap. A full-width child (such
    // as a summary line) drops onto its own row.
    rowsInline: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        '& > .MuiFormControl-root': {
            flex: '1 1 160px',
            marginTop: 0,
        },
    },
    // Inset panel for a subgroup such as "Immediate alerts".
    subgroup: {
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
    },
    subgroupHeader: {
        alignItems: 'center',
        display: 'flex',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    subgroupIcon: {
        color: theme.palette.primary.main,
    },
    subgroupLabel: {
        color: theme.palette.text.secondary,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
    },
    subgroupDescription: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
    },
    subgroupRows: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    // Base toggle card. A light border + padding are always present so the row
    // keeps a constant size (only the border color changes when it highlights).
    subgroupRow: {
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(1, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        cursor: 'pointer',
        // The field (MUI FormControl fieldset) is this card's first child, so its
        // `:first-child { marginTop }` rule would push it down — cancel it here.
        '& > .MuiFormControl-root': {
            marginTop: 0,
        },
        // The switch + title sit together in the FormControlLabel, so the title
        // starts past the switch. Indent the description (helper text) by the same
        // amount — switch width + gap — so it lines up under the title. Scoped to
        // the switch's own field so a nested dependent control (below) is untouched.
        '& > .MuiFormControl-root .MuiFormHelperText-root': {
            marginLeft: `calc(38px + ${theme.spacing(1.5)})`,
        },
        // Top-align the switch with the title instead of centring it against it.
        '& .MuiFormControlLabel-root': {
            alignItems: 'flex-start',
        },
    },
    // A dependent control (e.g. the custom-score slider) nested inside a toggle
    // card so the card's highlight covers it. Full width, own (default) cursor.
    subgroupRowNested: {
        marginTop: theme.spacing(1),
        cursor: 'default',
    },
    // Highlight a toggle card when its value is on (tinted bg + primary border).
    // Only colors change here — size stays fixed via `subgroupRow`.
    subgroupRowOn: {
        backgroundColor: alpha(theme.palette.primary.main, 0.06),
        borderColor: theme.palette.primary.main,
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(1),
    },
    submitError: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(1),
    },
});
